import React, { useState, useEffect } from "react";
import Loader from "../../components/Loader";
import ConfirmationModal from "../../components/ConfirmationModal";
import Edit from "../../assets/svgIcons/Edit.svg";
import ProfileEdit from "../../assets/svgIcons/ProfileEdit.svg";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import CityIllustration from "../../assets/icons/BuildingImage.png";
import { displayToast } from "../../utils/Toast";
import { updateProfile, fetchProfile, changePassword } from "../../apis/profile";
import { fetchTransactionByCode } from "../../apis/transaction";
import { Country, State } from "country-state-city";
import { uploadFile } from "../../utils/uploadFile";
import { useErrorHandler } from "../../services/ErrorHandler";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../../styles/pages/profile/Profile.css";

const DASHBOARD_FIELD_DEFAULTS = {
  employees: "",
  employees_subtitle: "",
  production_capacity: "",
  production_capacity_subtitle: "",
  iso_certification: "",
  iso_subtitle: "",
  open_work_orders: "",
  total_production: "",
  total_production_subtitle: "",
};

const getMappedDashboardFields = (orgData = {}) => ({
  employees: orgData.employees ?? "",
  employees_subtitle: orgData.employees_subtitle ?? "",
  production_capacity: orgData.production_capacity ?? "",
  production_capacity_subtitle: orgData.production_capacity_subtitle ?? "",
  iso_certification: orgData.iso_certification ?? "",
  iso_subtitle: orgData.iso_subtitle ?? "",
  total_production: orgData.total_production ?? "",
  total_production_subtitle: orgData.total_production_subtitle ?? "",
});

const fetchOpenWorkOrdersCount = async () => {
  try {
    const response = await fetchTransactionByCode({
      transactionCode: "WO",
      page: 1,
      pageSize: 1,
    });

    const totalCount = Number(
      response?.TotalCount ??
        response?.total_count ??
        response?.totalCount ??
        response?.count,
    );

    if (Number.isFinite(totalCount) && totalCount >= 0) {
      return String(totalCount);
    }

    if (Array.isArray(response?.TransactionList)) {
      return String(response.TransactionList.length);
    }

    return "";
  } catch (error) {
    return "";
  }
};

const CustomSelect = ({ value, options, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="custom-dd-select"
      tabIndex={0}
      onBlur={() => setIsOpen(false)}
    >
      <div
        className="custom-dd-select-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        {value || placeholder}
        <span className="profile-dropdownn-arrow">▼</span>
      </div>
      {isOpen && (
        <div className="custom-dd-select-options">
          {options.map((option) => (
            <div
              key={option.value}
              className="custom-dd-select-option"
              onClick={() => {
                onChange(option.label);
                setIsOpen(false);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SettingsProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isFromHomeEdit = Boolean(location.state?.fromHomeEdit);
  const [isEditing, setIsEditing] = useState(location.state?.editMode || false);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImageValid, setIsImageValid] = useState(true);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [organisation, setOrganisation] = useState(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showPasswordFields, setShowPasswordFields] = useState({
    current_password: false,
    new_password: false,
    confirm_password: false,
  });

  const { handleErrorResponse } = useErrorHandler();

  const [formData, setFormData] = useState({
    country: "",
    display_name: "",
    city: "",
    state: "",
    state_code: "",
    district: "",
    pincode: "",
    gstin: "",
    gstType: "",
    gstAbc: "",
    gstXyz: "",
    financialYear: "",
    email: "",
    phone: "",
    logo_url: "",
    pan: "",
    gst_no: "",
    contact: "",
    contact_person_name: "",
    designation: "",
    website: "",
    ...DASHBOARD_FIELD_DEFAULTS,
  });

  const fetchOrganizationData = async (silent = false) => {
    if (!silent) setIsLoading(true);
    try {
      const [response, openWorkOrders] = await Promise.all([
        fetchProfile(),
        fetchOpenWorkOrdersCount(),
      ]);
      if (response.description) {
        handleErrorResponse(response.description);
        if (!silent) displayToast(response.description, "error");
      } else {
        const dashboardFields = getMappedDashboardFields(response);

        setOrganisation(response);
        localStorage.setItem("organisation", JSON.stringify(response));
        setFormData({
          ...response,
          ...dashboardFields,
          open_work_orders: openWorkOrders,
          country: response.country || "",
          display_name: response.display_name || "",
        });
      }
    } catch (error) {
      handleErrorResponse(error);
      if (!silent) displayToast("Failed to load profile data", "error");
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useAutoRefresh(() => fetchOrganizationData(true), { enabled: !isEditing });

  const handleEdit = () => {
    navigate("/profile", { state: { editMode: true }, replace: true });
    setIsEditing(true);
  };

  async function handleLogoUpload() {
    try {
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "image/*";

      fileInput.addEventListener("change", async (event) => {
        const file = event.target.files[0];
        if (file) {
          setIsFileUploading(true);
          const uploadResponse = await uploadFile(file, "profile");

          if (uploadResponse?.error || uploadResponse?.description) {
            setIsFileUploading(false);
            handleErrorResponse(
              uploadResponse?.error || uploadResponse?.description,
            );
            return displayToast("Failed to upload logo", "error");
          }

          if (uploadResponse?.url) {
            setFormData((prevData) => ({
              ...prevData,
              logo_url: uploadResponse?.url,
            }));
            setIsFileUploading(false);
            setShowImageOptions(false);
            displayToast(
              "Logo selected. Click Save to update profile.",
              "success",
            );
          }
        }
      });

      fileInput.click();
    } catch (error) {
      console.error("Error selecting image:", error);
    } finally {
      setIsFileUploading(false);
      setShowImageOptions(false);
    }
  }

  function handleLogoCancel() {
    if (formData.logo_url) {
      setFormData((prevData) => ({
        ...prevData,
        logo_url: "",
      }));
    }
    setShowImageOptions(false);
  }

  useEffect(() => {
    const countryOptions = Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    }));
    setCountries(countryOptions);

    fetchOrganizationData();
  }, []);

  useEffect(() => {
    if (formData.country) {
      const countryCode = countries.find(
        (c) => c.label.toLowerCase() === formData.country.toLowerCase(),
      )?.value;

      if (countryCode) {
        const stateOptions = State.getStatesOfCountry(countryCode).map(
          (state) => ({
            value: state.isoCode,
            label: state.name,
          }),
        );
        setStates(stateOptions);
      }
    }
  }, [formData.country, countries]);

  useEffect(() => {
    if (organisation) {
      const dashboardFields = getMappedDashboardFields(organisation);
      setFormData({
        ...organisation,
        ...dashboardFields,
        open_work_orders: formData.open_work_orders,
        country: organisation.country ? organisation.country : "",
        display_name: organisation.display_name || "",
      });
    }
  }, [organisation]);

  useEffect(() => {
    if (location.state?.editMode) {
      setIsEditing(true);
    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "contact" && !/^\d*$/.test(value)) {
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name) => (value) => {
    if (name === "country") {
      setFormData({ ...formData, state: "", country: value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (passwordError) {
      setPasswordError("");
    }

    if (passwordSuccess) {
      setPasswordSuccess("");
    }
  };

  const resetPasswordForm = () => {
    setPasswordData({
      current_password: "",
      new_password: "",
      confirm_password: "",
    });
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    resetPasswordForm();
    setShowPasswordFields({
      current_password: false,
      new_password: false,
      confirm_password: false,
    });
  };

  const togglePasswordVisibility = (fieldName) => {
    setShowPasswordFields((prevState) => ({
      ...prevState,
      [fieldName]: !prevState[fieldName],
    }));
  };

  const validatePasswordForm = () => {
    if (!passwordData.current_password.trim()) {
      setPasswordError("Current password is required");
      return false;
    }

    if (!passwordData.new_password.trim()) {
      setPasswordError("New password is required");
      return false;
    }

    if (passwordData.new_password.trim().length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return false;
    }

    if (!passwordData.confirm_password.trim()) {
      setPasswordError("Confirm password is required");
      return false;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError("Passwords do not match");
      return false;
    }

    if (passwordData.current_password === passwordData.new_password) {
      setPasswordError("New password must be different from current password");
      return false;
    }

    setPasswordError("");
    return true;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) {
      return;
    }

    setIsPasswordLoading(true);

    try {
      const data = await changePassword(passwordData);
      const errorMessage =
        data?.error?.description || data?.error || data?.description;

      if (errorMessage) {
        setPasswordError(errorMessage);
        handleErrorResponse(errorMessage);
        displayToast(errorMessage, "error");
        return;
      }

      const successMessage = data?.message || "Password changed successfully";
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      setPasswordError("");
      setPasswordSuccess(successMessage);
      displayToast(successMessage, "success");
      setTimeout(() => {
        setShowChangePasswordModal(false);
      }, 1200);
    } catch (error) {
      handleErrorResponse(error);
      setPasswordError("There was an error while changing the password.");
      displayToast("There was an error while changing the password.", "error");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleCancel = () => {
    if (organisation) {
      const dashboardFields = getMappedDashboardFields(organisation);
      setFormData({
        ...organisation,
        ...dashboardFields,
        open_work_orders: formData.open_work_orders,
        country: organisation.country || "",
        display_name: organisation.display_name || "",
      });
    }
    navigate("/profile", { replace: true });
    setIsEditing(false);
  };

  const handleSave = async (updatedFormData) => {
    setShowConfirmModal(false);
    setIsLoading(true);
    setIsButtonLoading(true);
    try {
      const payload = {
        ...updatedFormData,
        ...DASHBOARD_FIELD_DEFAULTS,
        ...getMappedDashboardFields(updatedFormData),
      };
      delete payload.open_work_orders;

      const data = await updateProfile(payload);
      if (data.description || data.message) {
        handleErrorResponse(data.description);
        displayToast(
          data.description ? data.description : data.message,
          "error",
        );
      } else {
        const mergedData = {
          ...data,
          ...getMappedDashboardFields(data),
          open_work_orders: updatedFormData.open_work_orders,
        };

        localStorage.setItem("organisation", JSON.stringify(mergedData));

        setOrganisation(mergedData);
        setFormData({
          ...mergedData,
          ...getMappedDashboardFields(mergedData),
          open_work_orders: mergedData.open_work_orders,
          country: mergedData.country || "",
          display_name: mergedData.display_name || "",
        });
        displayToast("Profile updated successfully", "success");
        setIsEditing(false);

        if (isFromHomeEdit) {
          navigate("/", { replace: true });
        } else {
          navigate("/profile", { replace: true });
        }
      }
    } catch (error) {
      handleErrorResponse(error);
      displayToast("There was an error while saving your profile.", "error");
    } finally {
      setIsButtonLoading(false);
      setIsLoading(false);
    }
  };

  const renderField = (label, name, type = "text", readOnly = false) => {
    const options = name === "country" ? countries : states;

    return (
      <div className="profile-form-column">
        <label htmlFor={name}>{label}</label>
        {isEditing ? (
          name === "country" || name === "state" ? (
            <CustomSelect
              value={formData[name]}
              options={options}
              onChange={handleSelectChange(name)}
              placeholder={`Select ${label}`}
            />
          ) : type === "textarea" ? (
            <textarea
              id={name}
              name={name}
              value={formData[name] || ""}
              onChange={handleInputChange}
              className="profile-form-textarea"
              rows={3}
            />
          ) : (
            <input
              type={type}
              id={name}
              name={name}
              value={formData[name] || ""}
              onChange={handleInputChange}
              className="profile-form-input"
              readOnly={readOnly}
            />
          )
        ) : (
          <div className="profile-form-value">{formData[name]}</div>
        )}
      </div>
    );
  };

  return (
    <div className="settings-profile-container">
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Hero Banner */}
          <div className="profile-hero-banner">
            <div className="profile-hero-left">
              <div className="profile-hero-logo-wrapper">
                {isFileUploading ? (
                  <Loader />
                ) : (
                  <img
                    src={formData?.logo_url || ProfilePlaceholder}
                    alt="Profile"
                    className="profile-hero-logo"
                    onError={(e) => {
                      e.target.src = ProfilePlaceholder;
                      setIsImageValid(false);
                    }}
                    onLoad={() => setIsImageValid(true)}
                  />
                )}
                {isEditing && (
                  <div
                    className="profile-hero-logo-edit"
                    onClick={() => setShowImageOptions(true)}
                  >
                    <img src={ProfileEdit} alt="Edit" width="14" height="14" style={{ filter: "brightness(0) invert(1)" }} />
                  </div>
                )}
              </div>
              <div className="profile-hero-info">
                <h1 className="profile-hero-name">
                  {formData?.display_name || formData?.name || ""}
                </h1>
                {isEditing ? (
                  <p className="profile-hero-subtitle">
                    Update your company information and details
                  </p>
                ) : (
                  <button className="profile-edit-btn" type="button" onClick={handleEdit}>
                    <img src={Edit} alt="Edit" className="edit-icon" />
                    Edit
                  </button>
                )}
              </div>
            </div>
            <div className="profile-hero-right">
              <img src={CityIllustration} alt="" className="profile-hero-illustration" />
            </div>
          </div>

          <div className="profile-form">
            <div className="profile-section profile-security-section">
              <div className="section-header profile-security-header">
                <div className="section-icon-badge">
                  <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
                    <path d="M12 2 4 5v6c0 5.55 3.84 10.74 8 12 4.16-1.26 8-6.45 8-12V5l-8-3zm0 10.75a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5zm3 4.5H9v-.75c0-1.24 1.35-2.25 3-2.25s3 1.01 3 2.25v.75z" />
                  </svg>
                </div>
                <div className="profile-security-copy">
                  <h3 className="section-title">Account & Security</h3>
                  <p className="section-subtitle">Manage your password </p>
                </div>
                <button
                  type="button"
                  className="profile-save-btn profile-security-button"
                  onClick={() => setShowChangePasswordModal(true)}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-6 8.73V18a1 1 0 1 0 2 0v-1.27a2 2 0 1 0-2 0zM10 8V6a2 2 0 1 1 4 0v2h-4z" />
                  </svg>
                  Change Password
                </button>
              </div>
            </div>

            {/* Address Section */}
            <div className="profile-section">
              <div className="section-header">
                <div className="section-icon-badge">
                  <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="section-title">Address Information</h3>
                  <p className="section-subtitle">Manage your company address details</p>
                </div>
              </div>
              <div className="form-row">
                {renderField("Country", "country")}
                {renderField("City", "city")}
              </div>
              <div className="form-row">
                {renderField("State", "state")}
                {renderField("State Code", "state_code")}
              </div>
              <div className="form-row">
                {renderField("District", "district")}
                {renderField("Pin Code", "pincode")}
              </div>
              <div className="form-row">
                {renderField("Address", "address", "textarea")}
              </div>
            </div>

            {/* GST Section */}
            <div className="profile-section">
              <div className="section-header">
                <div className="section-icon-badge">
                  <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z" />
                  </svg>
                </div>
                <div>
                  <h3 className="section-title">GST Information</h3>
                  <p className="section-subtitle">Manage your GST details</p>
                </div>
              </div>
              <div className="form-row">
                {renderField("GST Number", "gst_no")}
                {renderField("GST registration status", "gst_registration_status")}
              </div>
              <div className="form-row">
                {renderField("GST supplier type", "gst_supplier_type")}
              </div>
            </div>

            {/* Other Info Section */}
            <div className="profile-section">
              <div className="section-header">
                <div className="section-icon-badge">
                  <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="section-title">Other Information</h3>
                  <p className="section-subtitle">Manage your company other details</p>
                </div>
              </div>
              <div className="form-row">
                {renderField("Company Name", "display_name")}
              </div>
              <div className="form-row">
                {renderField("PAN", "pan")}
                {renderField("Email", "email")}
              </div>
              <div className="form-row">
                {renderField("Phone Number", "contact")}
                {renderField("Contact Person Name", "contact_person_name")}
              </div>
              <div className="form-row">
                {renderField("Designation", "designation")}
                {renderField("Website", "website")}
              </div>
            </div>

            {/* Home Dashboard Section */}
            <div className="profile-section">
              <div className="section-header">
                <div className="section-icon-badge">
                  <svg viewBox="0 0 24 24" fill="white" width="18" height="18">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="section-title">Home Dashboard</h3>
                  <p className="section-subtitle">Manage your dashboard display data</p>
                </div>
              </div>
              <div className="form-row">
                {renderField("Employees", "employees")}
                {renderField("Employees Subtitle", "employees_subtitle")}
              </div>
              <div className="form-row">
                {renderField("Production Capacity", "production_capacity")}
                {renderField("Production Capacity Subtitle", "production_capacity_subtitle")}
              </div>
              <div className="form-row">
                {renderField("ISO Certification", "iso_certification")}
                {renderField("ISO Subtitle", "iso_subtitle")}
              </div>
              <div className="form-row">
                {renderField("Open Work Orders", "open_work_orders", "text", true)}
              </div>
              <div className="form-row">
                {renderField("Total Production", "total_production")}
                {renderField("Total Production Subtitle", "total_production_subtitle")}
              </div>
            </div>

            <ConfirmationModal
              isOpen={showConfirmModal}
              onClose={() => setShowConfirmModal(false)}
              onConfirm={() => handleSave(formData)}
              message="Are you sure you want to save these changes?"
            />
          </div>

          {/* Bottom Action Bar */}
          {isEditing && (
            <div className="profile-action-bar">
              {isButtonLoading ? (
                <div className="profile-loader-container">
                  <span className="profile-loader"></span>
                </div>
              ) : (
                <>
                  <button className="profile-cancel-btn" onClick={handleCancel}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                    </svg>
                    Cancel
                  </button>
                  <button className="profile-save-btn" onClick={() => handleSave(formData)}>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zm-5 16a3 3 0 0 1-3-3 3 3 0 0 1 3-3 3 3 0 0 1 3 3 3 3 0 0 1-3 3zm3-10H5V5h10v4z" />
                    </svg>
                    Save Changes
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
      <ConfirmationModal
        isOpen={showImageOptions}
        onClose={() => setShowImageOptions(false)}
        onConfirm={() => handleLogoUpload("Logo")}
        onCancel={() => handleLogoCancel()}
        message={
          formData.logo_url && isImageValid
            ? "Would you like to replace or remove the current image?"
            : "Do you want to upload a new image?"
        }
        confirmText="Choose Image"
        cancelText="Delete Image"
        showCancelButton={formData.logo_url && isImageValid}
      />
      {showChangePasswordModal && (
        <div
          className="profile-password-modal-overlay"
          onClick={handleCloseChangePasswordModal}
        >
          <div
            className="profile-password-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-password-modal-header">
              <div>
                <h3>Change Password</h3>
                <p>Enter your current password and set a new one.</p>
              </div>
              <button
                type="button"
                className="profile-password-modal-close"
                onClick={handleCloseChangePasswordModal}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <div className="profile-password-modal-body">
              <div className="profile-password-field">
                <label htmlFor="current_password">Current Password</label>
                <div className="profile-password-input-wrap">
                  <input
                    type={showPasswordFields.current_password ? "text" : "password"}
                    id="current_password"
                    name="current_password"
                    value={passwordData.current_password}
                    onChange={handlePasswordInputChange}
                    className="profile-form-input profile-password-input"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="profile-password-toggle"
                    onClick={() => togglePasswordVisibility("current_password")}
                  >
                    {showPasswordFields.current_password ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="profile-password-field">
                <label htmlFor="new_password">New Password</label>
                <div className="profile-password-input-wrap">
                  <input
                    type={showPasswordFields.new_password ? "text" : "password"}
                    id="new_password"
                    name="new_password"
                    value={passwordData.new_password}
                    onChange={handlePasswordInputChange}
                    className="profile-form-input profile-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="profile-password-toggle"
                    onClick={() => togglePasswordVisibility("new_password")}
                  >
                    {showPasswordFields.new_password ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="profile-password-field">
                <label htmlFor="confirm_password">Confirm New Password</label>
                <div className="profile-password-input-wrap">
                  <input
                    type={showPasswordFields.confirm_password ? "text" : "password"}
                    id="confirm_password"
                    name="confirm_password"
                    value={passwordData.confirm_password}
                    onChange={handlePasswordInputChange}
                    className="profile-form-input profile-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="profile-password-toggle"
                    onClick={() => togglePasswordVisibility("confirm_password")}
                  >
                    {showPasswordFields.confirm_password ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {(passwordError || passwordSuccess) && (
                <div
                  className={`profile-password-message ${passwordError ? "is-error" : "is-success"}`}
                >
                  {passwordError || passwordSuccess}
                </div>
              )}
            </div>

            <div className="profile-password-modal-actions">
              <button
                type="button"
                className="profile-cancel-btn"
                onClick={handleCloseChangePasswordModal}
                disabled={isPasswordLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="profile-save-btn"
                onClick={handleChangePassword}
                disabled={isPasswordLoading}
              >
                {isPasswordLoading ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsProfile;
